import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FaSearch, FaEdit, FaTrash, FaSave, FaLayerGroup,
    FaSort, FaSortUp, FaSortDown, FaPlus, FaCheckCircle, FaFileInvoice
} from 'react-icons/fa';
import {
    fetchPrestations,
    createPrestation,
    updatePrestation,
    deletePrestation
} from '../api';

const initialForm = { code: '', name: '', details: '', price: '' };

const PrestationPage = ({ user }) => {
    const navigate = useNavigate();
    const [prestations, setPrestations] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState(""); 
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

    // Fonction centralisée pour gérer l'affichage temporaire des messages
    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetchPrestations();
            setPrestations(res.data);
        } catch (err) { 
            showMessage('error', 'Impossible de charger les prestations'); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { load(); }, []);

    const filteredPrestations = prestations.filter(p => 
        p.code.toLowerCase().includes(search.toLowerCase()) ||
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    const sortedPrestations = React.useMemo(() => {
        let sortableItems = [...filteredPrestations];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredPrestations, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (name) => {
        if (sortConfig.key !== name) return <FaSort className="ms-1 text-muted opacity-50" />;
        return sortConfig.direction === 'asc' ? <FaSortUp className="ms-1 text-success" /> : <FaSortDown className="ms-1 text-success" />;
    };

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Sécurité côté backend simulée : blocage si tentative de modification par un non-admin
        if (editingId && user?.role !== 'admin') {
            showMessage('error', '⛔ Seul l\'administrateur peut modifier.');
            return;
        }

        if (!form.code || !form.name || form.price === '') {
            showMessage('error', 'Veuillez remplir les champs obligatoires.'); 
            return;
        }

        const payload = { code: form.code, name: form.name, details: form.details, price: Number(form.price) };
        
        try {
            if (editingId) {
                await updatePrestation(editingId, payload);
                showMessage('success', '✅ Mise à jour effectuée !');
            } else {
                await createPrestation(payload);
                showMessage('success', '✅ Prestation créée avec succès !');
            }
            setForm(initialForm); 
            setEditingId(null); 
            await load();
        } catch (err) { 
            showMessage('error', 'Erreur lors de la sauvegarde.'); 
        }
    };

    const startEdit = (p) => {
        setEditingId(p._id);
        setForm({ code: p.code, name: p.name, details: p.details || '', price: p.price.toString() });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (user?.role !== 'admin') return;
        if (!window.confirm('Supprimer cette prestation ?')) return;
        try {
            await deletePrestation(id);
            showMessage('success', '🗑️ Prestation supprimée');
            await load();
        } catch (err) { 
            showMessage('error', 'Erreur lors de la suppression.'); 
        }
    };

    const toggleSelect = (id) => {
        if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(itemId => itemId !== id));
        else setSelectedIds([...selectedIds, id]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(filteredPrestations.map(p => p._id));
        else setSelectedIds([]);
    };

    const goToInvoice = (type) => {
        if (selectedIds.length === 0) return;
        const dataToSend = prestations.filter(p => selectedIds.includes(p._id));
        const path = type === 'proforma' ? '/proformas' : '/factures';
        navigate(path, { state: { incomingPrestations: dataToSend } });
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            {/* HEADER SECTION */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaLayerGroup className="me-2 text-success"/> PRESTATIONS
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Catalogue des services et tarifications</p>
                </div>
                <div className="input-group input-group-sm w-100 shadow-sm" style={{maxWidth: '300px'}}>
                    <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                    <input 
                        type="text" className="form-control border-start-0" 
                        placeholder="Filtrer code ou nom..." value={search} onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* MESSAGES D'ALERTE */}
            {message && (
                <div className={`alert py-2 shadow-sm border-0 ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
                    {message.text}
                </div>
            )}

            {/* FORMULAIRE SECTION */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-success" style={{ letterSpacing: '1px' }}>
                    {editingId ? "MODIFIER LA PRESTATION" : "NOUVELLE PRESTATION"}
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-2">
                                <label className="fw-bold small mb-1 text-muted">CODE *</label>
                                <input name="code" value={form.code} onChange={handleChange} className="form-control form-control-sm" placeholder="ex: yyy" />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="fw-bold small mb-1 text-muted">NOM DE LA PRESTATION *</label>
                                <input name="name" value={form.name} onChange={handleChange} className="form-control form-control-sm" placeholder="Libellé complet" />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="fw-bold small mb-1 text-muted">SPÉCIFICATIONS</label>
                                <input name="details" value={form.details} onChange={handleChange} className="form-control form-control-sm" placeholder="Informations complémentaires" />
                            </div>
                            <div className="col-12 col-md-2">
                                <label className="fw-bold small mb-1 text-muted">PRIX (FCFA) *</label>
                                <input name="price" type="number" value={form.price} onChange={handleChange} className="form-control form-control-sm text-end fw-bold" placeholder="0" />
                            </div>
                        </div>
                        <div className="mt-4 d-flex flex-column flex-sm-row justify-content-end gap-2">
                            {editingId && (
                                <button type="button" className="btn btn-sm btn-outline-secondary fw-bold px-3" onClick={() => {setEditingId(null); setForm(initialForm)}}>
                                    ANNULER
                                </button>
                            )}
                            {(!editingId || (editingId && user?.role === 'admin')) && (
                                <button className={`btn btn-sm fw-bold px-4 text-white ${editingId ? 'btn-warning' : 'btn-success'}`} type="submit" style={!editingId ? { backgroundColor: '#1A7A38', borderColor: '#1A7A38' } : {}}>
                                    {editingId ? <><FaSave className="me-2"/>ENREGISTRER</> : <><FaPlus className="me-2"/>AJOUTER</>}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            {/* ACTIONS SUR SÉLECTION */}
            {selectedIds.length > 0 && (
                <div className="mb-4 p-3 bg-white shadow-sm rounded border-start border-warning border-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                    <span className="fw-bold text-dark"><FaCheckCircle className="text-warning me-2"/> {selectedIds.length} prestation(s) sélectionnée(s)</span>
                    <div className="d-flex flex-column flex-sm-row gap-2">
                        <button className="btn btn-sm btn-warning text-dark fw-bold px-3" onClick={() => goToInvoice('proforma')}>
                            <FaFileInvoice className="me-2"/> PROFORMA
                        </button>
                        <button className="btn btn-sm btn-success text-white fw-bold px-3" style={{ backgroundColor: '#1A7A38', borderColor: '#1A7A38' }} onClick={() => goToInvoice('facture')}>
                            <FaFileInvoice className="me-2"/> FACTURE
                        </button>
                    </div>
                </div>
            )}

            {/* TABLEAU SECTION */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="table-responsive" style={{ maxHeight: '50vh' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 border-bottom-0" style={{width: '40px'}}>
                                    <input type="checkbox" className="form-check-input" onChange={handleSelectAll} checked={selectedIds.length === filteredPrestations.length && filteredPrestations.length > 0}/>
                                </th>
                                <th className="py-3 border-bottom-0 cursor-pointer" onClick={() => requestSort('code')} style={{minWidth: '100px'}}>Code {getSortIcon('code')}</th>
                                <th className="py-3 border-bottom-0 cursor-pointer" onClick={() => requestSort('name')} style={{minWidth: '200px'}}>Prestation {getSortIcon('name')}</th>
                                <th className="py-3 border-bottom-0" style={{minWidth: '150px'}}>Spécifications</th>
                                <th className="py-3 border-bottom-0 text-end cursor-pointer" onClick={() => requestSort('price')} style={{minWidth: '120px'}}>Tarif (FCFA) {getSortIcon('price')}</th>
                                <th className="pe-3 py-3 border-bottom-0 text-end" style={{minWidth: '100px'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="text-center py-4 text-muted">Chargement des données...</td></tr>
                            ) : sortedPrestations.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-4 text-muted">Aucune prestation trouvée.</td></tr>
                            ) : (
                                sortedPrestations.map(p => (
                                    <tr key={p._id} className={selectedIds.includes(p._id) ? 'table-active' : ''}>
                                        <td className="ps-3">
                                            <input type="checkbox" className="form-check-input" checked={selectedIds.includes(p._id)} onChange={() => toggleSelect(p._id)}/>
                                        </td>
                                        <td><span className="badge bg-light text-dark border py-1 px-2">{p.code}</span></td>
                                        <td className="fw-bold text-wrap text-dark" style={{minWidth: '200px'}}>{p.name}</td>
                                        <td className="small text-wrap text-muted" style={{minWidth: '150px'}}>{p.details || '-'}</td>
                                        <td className="text-end fw-bold text-success">{p.price.toLocaleString()} F</td>
                                        <td className="text-end pe-3">
                                            {/* Les boutons de modification et suppression sont masqués pour les non-admins */}
                                            {user?.role === 'admin' && (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" onClick={() => startEdit(p)} title="Modifier">
                                                        <FaEdit size={14}/>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger rounded-1 py-1 px-2" onClick={() => handleDelete(p._id)} title="Supprimer">
                                                        <FaTrash size={14}/>
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PrestationPage;